if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          const index = event.target.dataset.index;
          const prevRaw = parseInt(event.target.getAttribute('value'), 10);
          const previousQty = Number.isFinite(prevRaw) ? prevRaw : 0;
          const parsedVal = parseInt(event.target.value, 10);
          if (parsedVal === 0) {
            this.startQueue(index, 0, previousQty);
          } else {
            this.validateQuantity(event);
          }
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
            this.handleErrorMessage();
          },
          { once: true }
        );
      }

      /**
       * Shows or hides the PLP bulk quick-add error alert
       * (`.product-form__error-message-wrapper` in card-product).
       */
      handleErrorMessage(errorMessage = false) {
        // Always re-query: cart section refreshes can replace this element's innerHTML.
        this.errorMessageWrapper = this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage = this.errorMessageWrapper.querySelector('.product-form__error-message');
        if (!this.errorMessage) return;

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      getCartAddErrorMessage(data) {
        if (!data) {
          return window.cartStrings?.error || 'Could not add items to cart';
        }

        if (typeof data.description === 'string' && data.description) {
          return data.description;
        }

        if (typeof data.message === 'string' && data.message) {
          return data.message;
        }

        if (Array.isArray(data.errors) && data.errors.length) {
          return data.errors.join(', ');
        }

        if (typeof data.errors === 'string' && data.errors) {
          return data.errors;
        }

        return window.cartStrings?.error || 'Could not add items to cart';
      }

      revertQuantityInput(variantId, quantity) {
        const input = this.querySelector(`#Quantity-${variantId}`) || this.input;
        if (!input) return;

        const safeQty = Number.isFinite(Number(quantity)) ? Math.max(0, Number(quantity)) : 0;
        input.value = safeQty;
        input.setAttribute('value', safeQty);
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk').dataset.id;
        }

        return this._sectionId;
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          fetch(`${this.getSectionsUrl()}?section_id=${this.sectionId}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(`#quick-add-bulk-${this.dataset.index}-${this.sectionId}`);
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      getSectionsUrl() {
        const pageParams = new URLSearchParams(window.location.search);
        const pageNumber = decodeURIComponent(pageParams.get('page') || '');

        return `${window.location.pathname}${pageNumber ? `?page=${pageNumber}` : ''}`;
      }

      /**
       * Mirrors BulkAdd.startQueue but stores `previousQty` (before commit) so the tax-bundle path can
       * compute deltas without blocking on `/cart.js` when UI cart qty matches server.
       */
      startQueue(id, quantity, previousQty) {
        const entry = { id, quantity };
        if (typeof previousQty === 'number' && !Number.isNaN(previousQty)) {
          entry.previousQty = Math.max(0, previousQty);
        }

        this.queue.push(entry);

        const interval = setInterval(() => {
          if (this.queue.length > 0) {
            if (!this.requestStarted) {
              this.sendRequest(this.queue);
            }
          } else {
            clearInterval(interval);
          }
        }, BulkAdd.ASYNC_REQUEST_DELAY);
      }

      validateQuantity(event) {
        const inputValue = parseInt(event.target.value, 10);
        const index = event.target.dataset.index;
        const prevRaw = parseInt(event.target.getAttribute('value'), 10);
        const previousQty = Number.isFinite(prevRaw) ? prevRaw : 0;

        if (inputValue < event.target.dataset.min) {
          this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
        } else if (inputValue > parseInt(event.target.max, 10)) {
          this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
        } else if (inputValue % parseInt(event.target.step, 10) !== 0) {
          this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
        } else {
          event.target.setCustomValidity('');
          event.target.reportValidity();
          this.handleErrorMessage();
          this.cleanErrorMessageOnType(event);
          event.target.setAttribute('value', inputValue);
          this.startQueue(index, inputValue, previousQty);
        }
      }

      async sendRequest(queue) {
        this.setRequestStarted(true);
        this.handleErrorMessage();

        // Collect tax items (handle + cost) from hidden inputs rendered in the card
        const taxHandleInputs = Array.from(this.querySelectorAll('.quick-add-bulk-tax-handle[data-tax-handle]'));
        // If there are no tax inputs at all, fall back to the base BulkAdd behavior
        if (!taxHandleInputs.length) {
          super.sendRequest(queue);
          return;
        }

        const taxItemsByHandle = {};

        taxHandleInputs.forEach((input) => {
          const handle = input.dataset.taxHandle || input.value;
          if (!handle) return;
          const cost = input.dataset.taxCost || '';
          if (!taxItemsByHandle[handle]) {
            taxItemsByHandle[handle] = [];
          }
          taxItemsByHandle[handle].push({ cost });
        });

        const uniqueTaxHandles = Object.keys(taxItemsByHandle);
        // If nothing valid was parsed, also fall back to a simple bulk quantity update
        if (!uniqueTaxHandles.length) {
          super.sendRequest(queue);
          return;
        }

        // Resolve each tax handle to a variant id using the product JSON endpoint
        const taxVariants = await Promise.all(
          uniqueTaxHandles.map(async (handle) => {
            const variantId = await this.getVariantIdForHandle(handle);
            return { handle, variantId };
          })
        );

        // Flatten into a list of tax items with concrete variant ids and costs
        const taxItems = [];
        taxVariants.forEach(({ handle, variantId }) => {
          if (!variantId) return;
          const entries = taxItemsByHandle[handle] || [];
          entries.forEach((entry) => {
            taxItems.push({
              id: variantId,
              cost: entry.cost || '',
            });
          });
        });

        // If we couldn't resolve any tax variants, fall back to a simple bulk quantity update
        if (!taxItems.length) {
          super.sendRequest(queue);
          return;
        }

        // Skip `/cart.js` entirely when every queue entry carries `previousQty` (normal PLP edits).
        // Prefetch only after tax resolves successfully so we don't request cart when falling back to super.sendRequest.
        const queueNeedsCartRead = queue.some((q) => typeof q.previousQty !== 'number');
        let cartJsonPromise = null;
        const ensureCartJsonFetched = () => {
          if (!cartJsonPromise) {
            cartJsonPromise = fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' })
              .then((response) => {
                if (!response.ok) throw new Error(`cart.js ${response.status}`);
                return response.json();
              })
              .catch((err) => {
                console.warn('quick-add-bulk: cart read failed', err);
                return { items: [] };
              });
          }
          return cartJsonPromise;
        };

        if (queueNeedsCartRead) {
          cartJsonPromise = ensureCartJsonFetched();
        }

        // `/cart/add.js` increments quantities. BulkAdd passes the input's *target* qty, so we must
        // add only (desired - current) for increases; decreases use `/cart/update.js` like the no-tax path.
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        let cartSnapshot = null;

        for (const queueItem of queue) {
          const desiredQty = queueItem.quantity;
          const parentVariantId = queueItem.id;

          let currentQty;
          const optimisticPrev = queueItem.previousQty;
          if (typeof optimisticPrev === 'number' && optimisticPrev >= 0) {
            currentQty = optimisticPrev;
          } else {
            cartSnapshot = cartSnapshot || (await ensureCartJsonFetched());
            currentQty = this.getPkParentVariantQty(cartSnapshot, parentVariantId);
          }

          const delta = desiredQty - currentQty;

          if (delta > 0) {
            const data = await this.addWithTaxItemsBulk(parentVariantId, delta, taxItems, cart);
            if (!data) {
              this.revertQuantityInput(parentVariantId, currentQty);
              break;
            }
            if (Array.isArray(data.items)) {
              cartSnapshot = data;
            }
          } else if (delta < 0) {
            const parsedState = await this.updateMultipleQty(
              { [parseInt(parentVariantId, 10)]: desiredQty },
              { deferRequestStartedReset: true }
            );
            if (parsedState && Array.isArray(parsedState.items)) {
              cartSnapshot = parsedState;
            }
          }
        }

        this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
        this.setRequestStarted(false);
      }

      /** Sum qty for parent lines only (not PK tax child lines). `cartSnapshot` is `/cart.js` JSON. */
      getPkParentVariantQty(cartSnapshot, variantId) {
        const vid = Number(variantId);
        if (!cartSnapshot?.items?.length || !Number.isFinite(vid)) return 0;

        return cartSnapshot.items.reduce((sum, line) => {
          if (line.parent_relationship?.parent_key) return sum;
          if (Number(line.variant_id) !== vid) return sum;
          return sum + (Number(line.quantity) || 0);
        }, 0);
      }

      async getVariantIdForHandle(handle) {
        if (!handle) return null;

        window.__pkTaxVariantHandleCache = window.__pkTaxVariantHandleCache || {};
        const cache = window.__pkTaxVariantHandleCache;

        if (cache[handle]) {
          return cache[handle];
        }

        try {
          const response = await fetch(
            `${window.location.origin}/products/${encodeURIComponent(handle)}.json`
          );
          if (!response.ok) return null;

          const data = await response.json();
          const variant = data?.product?.variants?.[0];

          if (variant?.id) {
            cache[handle] = variant.id;
            return variant.id;
          }
        } catch (e) {
          console.error('Failed to fetch tax product JSON for handle:', handle, e);
        }

        return null;
      }

      async addWithTaxItemsBulk(parentVariantId, quantity, taxItems, cart = null) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        cart = cart || document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        const nestedFormData = new FormData();
        nestedFormData.append('items[0][id]', parentVariantId);
        nestedFormData.append('items[0][quantity]', quantity);
        nestedFormData.append(
          'items[0][properties][_pk_tax_items]',
          JSON.stringify(this.buildTaxMetadata(taxItems))
        );

        taxItems.forEach((tax, index) => {
          const idx = index + 1;
          nestedFormData.append(`items[${idx}][id]`, tax.id);
          nestedFormData.append(`items[${idx}][quantity]`, quantity);
          nestedFormData.append(`items[${idx}][parent_id]`, parentVariantId);
          if (tax.cost) {
            nestedFormData.append(`items[${idx}][properties][_cost]`, tax.cost);
          }
        });

        if (cart && typeof cart.getSectionsToRender === 'function') {
          nestedFormData.append(
            'sections',
            cart.getSectionsToRender().map((section) => section.id)
          );
        } else {
          nestedFormData.append(
            'sections',
            this.getSectionsToRender().map((section) => section.section)
          );
        }
        nestedFormData.append('sections_url', window.location.pathname);

        config.body = nestedFormData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config);
          let data = null;

          try {
            data = await response.json();
          } catch (parseErr) {
            console.error('quick-add-bulk: cart/add parse failed', parseErr);
            this.handleErrorMessage(this.getCartAddErrorMessage(null));
            return null;
          }

          // Shopify Ajax Cart returns `{ status, message, description }` on failure (often 422).
          if (!response.ok || data?.status || data?.errors) {
            const message = this.getCartAddErrorMessage(data);
            this.handleErrorMessage(message);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'quick-add-bulk',
              productVariantId: parentVariantId,
              errors: data?.errors || data?.description,
              message: data?.message || message,
            });
            return null;
          }

          this.handleErrorMessage();

          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(data);
          } else {
            // Fallback: re-render PLP quick-add block + cart sections using the returned section HTML
            const ids = [String(parentVariantId)];
            this.renderSections(data, ids);
          }

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', cartData: data });

          return data;
        } catch (fetchErr) {
          console.error('quick-add-bulk: cart/add failed', fetchErr);
          this.handleErrorMessage(this.getCartAddErrorMessage(null));
          return null;
        }
      }

      buildTaxMetadata(taxItems) {
        return taxItems
          .filter((tax) => tax && tax.id)
          .map((tax) => ({
            id: String(tax.id),
            cost: tax.cost || '',
          }));
      }

      updateMultipleQty(items, options = {}) {
        const deferRequestStartedReset = options.deferRequestStartedReset === true;
        this.selectProgressBar()?.classList.remove('hidden');

        const ids = Object.keys(items);

        const cartNotification = document.querySelector('cart-notification');
        const cartDrawer = document.querySelector('cart-drawer');

        // IMPORTANT: `cart-notification.renderContents()` expects `parsedState.sections`
        // to include keys like `cart-notification-product` / `cart-notification-button`.
        // For the no-tax-item path we must request those sections explicitly.
        const cartSections = new Set();
        if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
          cartNotification.getSectionsToRender().forEach((section) => cartSections.add(section.id));
        } else if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          cartDrawer.getSectionsToRender().forEach((section) => cartSections.add(section.id));
        }

        const body = JSON.stringify({
          updates: items,
          sections: [this.sectionId, ...cartSections].filter(Boolean),
          sections_url: this.getSectionsUrl(),
        });

        return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => {
            return response.text();
          })
          .then((state) => {
            let parsedState = null;
            try {
              parsedState = JSON.parse(state);
            } catch (parseErr) {
              console.error('quick-add-bulk: cart/update parse failed', parseErr);
              return null;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });

            // Use renderContents when available so keyed cart-notification content updates too.
            if (cartNotification && typeof cartNotification.renderContents === 'function') {
              cartNotification.renderContents(parsedState);
            } else if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(parsedState);
            } else if (cartNotification && typeof cartNotification.open === 'function') {
              cartNotification.open();
            } else if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              // Last-resort fallback: update DOM sections if renderContents isn't available.
              try {
                this.renderSections(parsedState, ids);
              } catch (e) {
                console.error('quick-add-bulk renderSections failed:', e);
              }
            }

            return parsedState;
          })
          .catch((fetchErr) => {
            console.warn('quick-add-bulk: cart/update failed', fetchErr);
            // Commented out for now and will be fixed when BE issue is done https://github.com/Shopify/shopify/issues/440605
            // e.target.setCustomValidity(error);
            // e.target.reportValidity();
            // this.resetQuantityInput(ids[index]);
            // this.selectProgressBar().classList.add('hidden');
            // e.target.select();
            // this.cleanErrorMessageOnType(e);
            return null;
          })
          .finally(() => {
            this.selectProgressBar()?.classList.add('hidden');
            if (!deferRequestStartedReset) {
              this.setRequestStarted(false);
            }
          });
      }

      getSectionsToRender() {
        return [
          {
            id: `quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
            section: this.sectionId,
            selector: `#quick-add-bulk-${this.dataset.index}-${this.sectionId}`,
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
          {
            id: 'CartDrawer',
            selector: '.drawer__inner',
            section: 'cart-drawer',
          },
        ];
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (section.section === 'cart-drawer') {
            const itemsLength = Array.isArray(parsedState.items) ? parsedState.items.length : 0;
            sectionElement?.closest('cart-drawer')?.classList.toggle('is-empty', itemsLength === 0);
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            const sectionHtml = parsedState.sections?.[section.section];
            if (sectionHtml == null) return;

            elementToReplace.innerHTML = this.getSectionInnerHTML(
              sectionHtml,
              section.selector
            );
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }

      // Safer version of BulkAdd.getSectionInnerHTML: cart update responses don't always
      // contain the exact selector wrapper we expect (e.g. `.shopify-section`).
      // When the selector isn't found, we fall back to `.shopify-section` or the document body.
      getSectionInnerHTML(html, selector = '.shopify-section') {
        if (!html) return '';

        const doc = new DOMParser().parseFromString(html, 'text/html');

        if (selector) {
          const targeted = doc.querySelector(selector);
          if (targeted) return targeted.innerHTML;
        }

        const sectionRoot = doc.querySelector('.shopify-section') || doc.body;
        return sectionRoot ? sectionRoot.innerHTML : '';
      }
    }
  );
}
